import { useTranslation } from 'react-i18next';

import { formatPeriod } from '@/shared/lib/datetime';

import type { Card } from '@/entities/board/types';
import styles from './DeadlineBadge.module.css';

interface Props {
  card: Card;
  className?: string;
  /** Makes the badge a button (quick deadline edit on a tile). */
  onClick?: () => void;
}

/**
 * Compact deadline marker (`10 окт · 18:30`, or `5 окт → 10 окт` for a period) for a card. The
 * state comes from the server and is never shown by color alone: every state has an icon and a
 * text alternative (overdue red ⚠, today yellow, tomorrow/later blue), and a completed card is
 * never presented as overdue.
 */
export function DeadlineBadge({ card, className, onClick }: Props) {
  const { t, i18n } = useTranslation();
  const { deadline } = card;
  if (!deadline) return null;
  const done = card.completed_at !== null;
  const state = card.is_overdue ? 'overdue' : deadline.state;
  const when = formatPeriod(deadline, i18n.language);
  const classes = [
    styles.badge,
    styles[state],
    done && styles.done,
    onClick && styles.action,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const content = (
    <>
      <span aria-hidden>{card.is_overdue ? '⚠' : state === 'today' ? '●' : '📅'}</span>
      <span className="visually-hidden">
        {`${t(`deadline.state.${card.is_overdue ? 'overdue' : state}`)}${done ? ` (${t('timeline.completed')})` : ''}: `}
      </span>
      {when}
    </>
  );
  const title = onClick ? t('deadline.quickEdit') : t('deadline.badge', { when });
  const common = {
    className: classes,
    'data-testid': 'deadline-badge',
    'data-state': state,
    title,
  };
  return onClick ? (
    <button type="button" {...common} onClick={onClick}>
      {content}
    </button>
  ) : (
    <span {...common}>{content}</span>
  );
}
